import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/lib";

const alertVariants = cva(
  `
    relative
    w-full rounded-lg border p-4 text-left text-sm [&>svg]:absolute
    [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground
    [&>svg+div]:translate-y-[-3px]
    [&>svg~*]:pl-7
  `,
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: `
          border-destructive/50 text-destructive
          dark:border-destructive
          [&>svg]:text-destructive
        `,
        warning: `
          border-yellow-500 bg-yellow-100/10 text-yellow-500
          [&>svg]:text-yellow-500
        `,
        error: `
          border-red-500 bg-red-100/10 text-red-500
          [&>svg]:text-red-500
        `,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = ({
  ref,
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
);
Alert.displayName = "Alert";

const AlertTitle = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
  ref?: React.RefObject<HTMLParagraphElement>;
}) => (
  <h5
    ref={ref}
    className={cn("mb-2 leading-none font-semibold tracking-tight", className)}
    {...props}
  />
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.RefObject<HTMLParagraphElement>;
}) => (
  <div
    ref={ref}
    className={cn(
      `
        text-sm
        dark:opacity-60
        [&_p]:leading-relaxed
      `,
      className,
    )}
    {...props}
  />
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle };
